import { z } from 'zod';
import { marketPresentationSchema } from './market-presentation.js';
import { opportunityRoundSchema, opportunitySourceTypeSchema, opportunityStateSchema, verifierStateForHistorySchema, } from './opportunity-history.js';
const idStringSchema = z.string().trim().min(1);
const timestampSchema = z.string().trim().min(1);
export const opportunityFeedQuerySchema = z.object({
    sourceType: opportunitySourceTypeSchema.optional(),
    opportunityType: z.string().trim().min(1).optional(),
    state: opportunityStateSchema.optional(),
    verifiedOnly: z.coerce.boolean().optional().default(false),
    mappedOnly: z.coerce.boolean().optional().default(false),
    limit: z.coerce.number().int().positive().max(100).optional().default(50),
});
export const opportunityLinkSchema = z.object({
    label: idStringSchema,
    href: z.string().url(),
});
export const opportunityFeedItemSchema = z.object({
    opportunityId: idStringSchema,
    opportunityKey: idStringSchema,
    currentRoundId: idStringSchema.nullable(),
    sourceType: opportunitySourceTypeSchema,
    opportunityType: idStringSchema,
    eventId: idStringSchema,
    outcomeId: idStringSchema,
    outcomeLabel: idStringSchema,
    title: idStringSchema,
    state: opportunityStateSchema,
    visible: z.boolean(),
    verifierState: verifierStateForHistorySchema.nullable(),
    rankScoreBps: z.number().int().min(0).max(10000),
    decisionSupportScore: z.number().int().min(0).max(100).nullable().default(null),
    scoreBreakdown: z.object({
        whaleIntensity: z.number().int().min(0).max(100).nullable().default(null),
        marketDislocationStrength: z.number().int().min(0).max(100).nullable().default(null),
        oddsFreshness: z.number().int().min(0).max(100).nullable().default(null),
    }).default({
        whaleIntensity: null,
        marketDislocationStrength: null,
        oddsFreshness: null,
    }),
    mapped: z.boolean().default(false),
    currentEdgeBps: z.number().int().min(-10000).max(10000).nullable(),
    firstVisibleAt: timestampSchema.nullable(),
    lastDecisionAt: timestampSchema,
    expiresAt: timestampSchema.nullable(),
    sourceLinks: z.array(opportunityLinkSchema),
});
export const radarEvidenceStatusSchema = z.enum(['available', 'missing', 'stale', 'pending']);
const radarScoreComponentSchema = z.object({
    key: z.enum(['whaleIntensity', 'marketDislocationStrength', 'oddsFreshness']),
    label: idStringSchema,
    value: z.number().int().min(0).max(100).nullable(),
    status: radarEvidenceStatusSchema,
});
export const radarResearchLinkSchema = z.object({
    kind: z.enum(['team_profile', 'head_to_head', 'evidence_explorer']),
    label: idStringSchema,
    href: idStringSchema,
});
export const opportunityDetailSchema = opportunityFeedItemSchema.extend({
    firstDetectedAt: timestampSchema,
    lastSourceEventAt: timestampSchema,
    latestSourceEventId: idStringSchema.nullable(),
    latestDecisionEventId: idStringSchema.nullable(),
    latestVerificationAttemptId: idStringSchema.nullable(),
    currentRound: opportunityRoundSchema.nullable(),
    recentRounds: z.array(opportunityRoundSchema),
    scoreEvidence: z.object({
        status: radarEvidenceStatusSchema,
        decisionSupportScore: z.number().int().min(0).max(100).nullable(),
        components: z.array(radarScoreComponentSchema),
    }).nullable().default(null),
    marketEvidence: z.object({
        status: radarEvidenceStatusSchema,
        title: idStringSchema,
        outcomeLabel: idStringSchema,
        currentEdgeBps: z.number().int().min(-10000).max(10000).nullable(),
        lastSourceEventAt: timestampSchema,
        sourceLinks: z.array(opportunityLinkSchema),
    }).nullable().default(null),
    oddsEvidence: z.object({
        status: radarEvidenceStatusSchema,
        aggregateReference: z.string().trim().min(1).nullable(),
        bookmakerSpreadBps: z.number().int().min(0).max(10000).nullable(),
        lastUpdatedAt: timestampSchema.nullable(),
    }).nullable().default(null),
    verifierEvidence: z.object({
        status: radarEvidenceStatusSchema,
        verifierState: verifierStateForHistorySchema.nullable(),
        latestVerificationAttemptId: idStringSchema.nullable(),
    }).nullable().default(null),
    historicalContextEvidence: z.object({
        status: radarEvidenceStatusSchema,
        teams: z.array(idStringSchema),
        summary: z.string().trim().min(1).nullable(),
    }).nullable().default(null),
    researchLinks: z.array(radarResearchLinkSchema).default([]),
});
export const radarEventTypeSchema = z.enum(['match', 'prop']);
export const radarEventTabSchema = z.enum(['matches', 'props', 'completed']);
export const radarMappingSourceSchema = z.enum(['fixture_mapping', 'mapped_prop_market']);
export const radarMatchMarketSectionSchema = z.enum(['moneyline', 'spread', 'total_goals']);
export const radarPropMarketTypeSchema = z.enum(['tournament_winner', 'group_winner', 'advance_from_group', 'reach_final']);
export const radarEnhancementFilterSchema = z.enum(['price_gap', 'large_fund', 'trading_activity']);
export const radarMarketDataStateSchema = z.enum(['comparison_available', 'comparison_pending', 'market_only']);
export const radarMatchStatusSchema = z.enum(['active', 'completed']);
export const radarMatchSortSchema = z.enum(['default', 'recently-finished']);
export const radarCompletedMatchCoverageStateSchema = z.enum(['available', 'missing', 'stale', 'pending']);
export const radarEventsQuerySchema = z.object({
    tab: radarEventTabSchema.optional().default('matches'),
    status: radarMatchStatusSchema.optional().default('active'),
    sort: radarMatchSortSchema.optional().default('default'),
    enhancementFilter: radarEnhancementFilterSchema.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    pageSize: z.coerce.number().int().positive().max(100).optional().default(20),
    competitionCode: idStringSchema.optional(),
    season: z.coerce.number().int().positive().optional(),
});
export const radarEventParamsSchema = z.object({
    eventId: idStringSchema,
});
export const radarRepresentativePriceGapSchema = z.object({
    outcomeId: idStringSchema,
    outcomeLabel: idStringSchema,
    polymarketProbabilityBps: z.number().int().min(0).max(10000).nullable(),
    referenceLabel: idStringSchema,
    referenceProbabilityBps: z.number().int().min(0).max(10000).nullable(),
    signedDifferenceBps: z.number().int().min(-10000).max(10000),
    updatedAt: timestampSchema.nullable(),
    representativeBookmaker: z.object({
        bookmakerName: idStringSchema,
        probabilityBps: z.number().int().min(0).max(10000),
        decimalOdds: z.number().finite().gt(1).nullable(),
        selectedReason: z.enum(['reference_closest_to_aggregate', 'all_fresh_closest_to_aggregate']),
    }).nullable(),
});
export const radarMarketSectionSummarySchema = z.object({
    section: radarMatchMarketSectionSchema,
    label: idStringSchema,
    outcomeCount: z.number().int().min(0),
});
export const radarTradingActivitySummarySchema = z.object({
    volume24hrUsd: z.number().finite().min(0).nullable(),
    liquidityUsd: z.number().finite().min(0).nullable(),
    label: idStringSchema.nullable(),
});
export const radarLargeFundSummarySchema = z.object({
    outcomeLabel: idStringSchema.nullable(),
    label: idStringSchema,
});
export const radarCompletedMatchCoverageSchema = z.object({
    fixture: radarCompletedMatchCoverageStateSchema,
    statisticsFullTime: radarCompletedMatchCoverageStateSchema,
    statisticsHalfTime: radarCompletedMatchCoverageStateSchema,
    events: radarCompletedMatchCoverageStateSchema,
    lineups: radarCompletedMatchCoverageStateSchema,
    players: radarCompletedMatchCoverageStateSchema,
}).strict();
export const radarCompletedMatchSourceTraceSchema = z.object({
    provider: idStringSchema,
    endpoint: idStringSchema,
    payloadId: idStringSchema.nullable(),
    contentHash: idStringSchema.nullable(),
    fetchedAt: timestampSchema.nullable(),
}).strict();
export const radarCompletedMatchScoreSchema = z.object({
    home: z.number().int().min(0).nullable(),
    away: z.number().int().min(0).nullable(),
    winnerTeamId: z.number().int().nullable(),
}).strict();
export const radarCompletedCompetitionMetadataSchema = z.object({
    competitionCode: idStringSchema,
    displayName: idStringSchema,
    seasonLabel: idStringSchema,
    format: z.enum(['tournament', 'domestic_league', 'hybrid_cup']),
    stageLabel: idStringSchema.nullable(),
    roundLabel: idStringSchema.nullable(),
    matchweek: z.number().int().positive().nullable(),
    leg: z.enum(['first', 'second', 'single']).nullable(),
}).strict();
export const radarCompletedMatchSummarySchema = z.object({
    fixtureId: z.number().int().positive(),
    statusShort: idStringSchema,
    kickoffAt: timestampSchema,
    stage: idStringSchema.nullable(),
    competition: radarCompletedCompetitionMetadataSchema,
    venue: idStringSchema.nullable(),
    score: radarCompletedMatchScoreSchema,
    coverage: radarCompletedMatchCoverageSchema,
    sourceTrace: z.array(radarCompletedMatchSourceTraceSchema).default([]),
}).strict();
export const radarCompletedMatchTeamSchema = z.object({
    id: z.number().int().nullable(),
    name: idStringSchema,
    logoUrl: z.string().url().nullable().optional(),
}).strict();
export const radarCompletedMatchStatisticSchema = z.object({
    key: idStringSchema,
    label: idStringSchema,
    homeValue: z.string().nullable(),
    awayValue: z.string().nullable(),
}).strict();
export const radarCompletedMatchTimelineEventSchema = z.object({
    minute: z.number().int().min(0).nullable(),
    extraMinute: z.number().int().min(0).nullable(),
    teamId: z.number().int().nullable(),
    teamName: idStringSchema.nullable(),
    playerName: idStringSchema.nullable(),
    assistName: idStringSchema.nullable(),
    type: idStringSchema,
    detail: idStringSchema.nullable(),
}).strict();
export const radarCompletedMatchLineupPlayerSchema = z.object({
    playerId: z.number().int().nullable(),
    name: idStringSchema,
    number: z.number().int().nullable(),
    position: idStringSchema.nullable(),
    grid: idStringSchema.nullable(),
}).strict();
export const radarCompletedMatchLineupSchema = z.object({
    teamId: z.number().int().nullable(),
    teamName: idStringSchema,
    formation: idStringSchema.nullable(),
    coachName: idStringSchema.nullable(),
    startXI: z.array(radarCompletedMatchLineupPlayerSchema),
    substitutes: z.array(radarCompletedMatchLineupPlayerSchema),
}).strict();
export const radarCompletedMatchPlayerStatSchema = z.object({
    teamId: z.number().int().nullable(),
    teamName: idStringSchema,
    playerId: z.number().int().nullable(),
    playerName: idStringSchema,
    position: idStringSchema.nullable(),
    minutes: z.number().int().min(0).nullable(),
    rating: z.string().nullable(),
    goals: z.number().int().min(0).nullable(),
    assists: z.number().int().min(0).nullable(),
    shots: z.number().int().min(0).nullable(),
    passes: z.number().int().min(0).nullable(),
    tackles: z.number().int().min(0).nullable(),
    duels: z.number().int().min(0).nullable(),
    dribbles: z.number().int().min(0).nullable(),
    fouls: z.number().int().min(0).nullable(),
    cards: z.object({
        yellow: z.number().int().min(0).nullable(),
        red: z.number().int().min(0).nullable(),
    }).strict(),
}).strict();
export const radarCompletedMatchDetailSchema = radarCompletedMatchSummarySchema.extend({
    teams: z.object({
        home: radarCompletedMatchTeamSchema,
        away: radarCompletedMatchTeamSchema,
    }).strict(),
    statistics: z.object({
        fullTime: z.array(radarCompletedMatchStatisticSchema),
        firstHalf: z.array(radarCompletedMatchStatisticSchema).nullable(),
        secondHalf: z.array(radarCompletedMatchStatisticSchema).nullable(),
    }).strict(),
    timeline: z.array(radarCompletedMatchTimelineEventSchema),
    lineups: z.array(radarCompletedMatchLineupSchema),
    players: z.array(radarCompletedMatchPlayerStatSchema),
}).strict();
export const radarLargeTradeSummarySchema = z.object({
    count: z.number().int().min(0),
    totalNotionalUsd: z.number().finite().min(0),
    latestTradeAt: timestampSchema.nullable(),
    outcomeLabel: idStringSchema.nullable(),
    marketSection: z.enum(['moneyline', 'spread', 'total_goals', 'prop']).nullable(),
    lineValue: z.string().nullable(),
});
export const radarEventOutcomeComparisonSchema = z.object({
    status: z.enum(['available', 'unavailable']),
    referenceLabel: idStringSchema.nullable(),
    referenceProbabilityBps: z.number().int().min(0).max(10000).nullable(),
    signedDifferenceBps: z.number().int().min(-10000).max(10000).nullable(),
    expectedValueBps: z.number().int().min(-10000).max(10000).nullable(),
    sourceLabels: z.array(idStringSchema).default([]),
    updatedAt: timestampSchema.nullable(),
    representativeBookmaker: z.object({
        bookmakerName: idStringSchema,
        probabilityBps: z.number().int().min(0).max(10000),
        decimalOdds: z.number().finite().gt(1).nullable(),
        selectedReason: z.enum(['reference_closest_to_aggregate', 'all_fresh_closest_to_aggregate']),
    }).nullable(),
});
export const radarEventOutcomePolymarketRawSchema = z.object({
    marketId: idStringSchema.nullable(),
    marketQuestion: idStringSchema.nullable(),
    outcomeLabel: idStringSchema.nullable(),
    outcomeIndex: z.number().int().min(0).nullable(),
    conditionId: idStringSchema.nullable(),
    clobTokenId: idStringSchema.nullable(),
    price: z.number().finite().min(0).max(1).nullable(),
    lastTradePrice: z.number().finite().min(0).max(1).nullable(),
    bestBid: z.number().finite().min(0).max(1).nullable(),
    bestAsk: z.number().finite().min(0).max(1).nullable(),
    updatedAt: timestampSchema.nullable(),
}).nullable();
export const radarEventOutcomeApiFootballMappingSchema = z.object({
    mappingId: idStringSchema,
    fixtureId: z.number().int().positive(),
    betId: z.number().int().positive(),
    betName: idStringSchema,
    providerValue: idStringSchema,
    side: z.enum(['home', 'draw', 'away']).nullable(),
    lineValue: idStringSchema.nullable(),
}).nullable();
export const radarEventOutcomeBookmakerOddsSchema = z.object({
    evidenceId: idStringSchema,
    bookmakerId: z.number().int().positive(),
    bookmakerName: idStringSchema,
    betId: z.number().int().positive(),
    providerValue: idStringSchema,
    decimalOdds: z.number().finite().gt(1),
    impliedProbabilityBps: z.number().int().min(0).max(10000),
    normalizedImpliedProbabilityBps: z.number().int().min(0).max(10000).nullable(),
    polymarketProbabilityBps: z.number().int().min(0).max(10000),
    polymarketDecimalOdds: z.number().finite().gt(1).nullable(),
    fetchedAt: timestampSchema,
    providerTimestamp: timestampSchema.nullable(),
    freshnessState: z.enum([
        'fresh',
        'stale',
        'missing_equivalent',
        'provider_failed',
        'rate_limited',
        'invalid_payload',
        'unsupported_market',
    ]),
});
export const radarEventOutcomeSchema = z.object({
    opportunityId: idStringSchema,
    outcomeId: idStringSchema,
    outcomeLabel: idStringSchema,
    marketSection: radarMatchMarketSectionSchema.nullable(),
    propMarketType: radarPropMarketTypeSchema.nullable(),
    polymarketMarketId: idStringSchema.nullable(),
    polymarketProbabilityBps: z.number().int().min(0).max(10000).nullable(),
    comparison: radarEventOutcomeComparisonSchema.nullable(),
    tradingActivity: radarTradingActivitySummarySchema.nullable(),
    largeFundSummary: radarLargeFundSummarySchema.nullable(),
    largeTradeSummary: radarLargeTradeSummarySchema.nullable().optional(),
    lineValue: z.string().nullable().optional(),
    isDefaultLine: z.boolean().optional(),
    polymarket: radarEventOutcomePolymarketRawSchema.optional(),
    apiFootballMapping: radarEventOutcomeApiFootballMappingSchema.optional(),
    bookmakerOdds: z.array(radarEventOutcomeBookmakerOddsSchema).optional(),
    opportunityDetailUrl: idStringSchema,
});
export const radarEventMarketSectionSchema = z.object({
    section: radarMatchMarketSectionSchema,
    label: idStringSchema,
    outcomes: z.array(radarEventOutcomeSchema),
});
export const radarEventTeamSchema = z.object({
    id: z.number().int().nullable(),
    name: idStringSchema,
    logoUrl: z.string().url().nullable(),
}).strict();
export const radarEventTeamsSchema = z.object({
    home: radarEventTeamSchema,
    away: radarEventTeamSchema,
}).strict();
export const radarEventListItemSchema = z.object({
    eventId: idStringSchema,
    eventType: radarEventTypeSchema,
    mappingSource: radarMappingSourceSchema,
    title: idStringSchema,
    subtitle: idStringSchema.nullable(),
    startsAt: timestampSchema.nullable(),
    polymarketUrl: z.string().url().nullable(),
    marketSections: z.array(radarEventMarketSectionSchema),
    propMarketType: radarPropMarketTypeSchema.nullable(),
    mappedOutcomeCount: z.number().int().min(0),
    marketDataState: radarMarketDataStateSchema,
    representativePriceGap: radarRepresentativePriceGapSchema.nullable(),
    largeFundSummary: radarLargeFundSummarySchema.nullable(),
    largeTradeSummary: radarLargeTradeSummarySchema.nullable().optional(),
    tradingActivity: radarTradingActivitySummarySchema.nullable(),
    detailUrl: idStringSchema,
    researchLink: radarResearchLinkSchema.nullable(),
    teams: radarEventTeamsSchema.nullable().optional(),
    completedSummary: radarCompletedMatchSummarySchema.nullable().optional(),
    marketPresentation: marketPresentationSchema.optional(),
});
export const radarEventsResponseSchema = z.object({
    items: z.array(radarEventListItemSchema),
    nextCursor: z.string().nullable(),
});
export const radarEventWorldCupContextSchema = z.object({
    fixtureId: z.number().int().positive(),
    venueId: z.number().int().positive().nullable(),
    homeTeamId: z.number().int().positive(),
    awayTeamId: z.number().int().positive(),
    kickoffTimestampMs: z.number().int().positive(),
});
export const radarEventDetailSchema = radarEventListItemSchema.extend({
    outcomes: z.array(radarEventOutcomeSchema),
    sections: z.array(radarEventMarketSectionSchema),
    marketRules: z.string().trim().min(1).nullable(),
    researchLinks: z.array(radarResearchLinkSchema),
    worldcup: radarEventWorldCupContextSchema.nullable().optional(),
    completedMatch: radarCompletedMatchDetailSchema.nullable().optional(),
});
export const publicRadarEventListItemSchema = radarEventListItemSchema.pick({
    eventId: true,
    eventType: true,
    mappingSource: true,
    title: true,
    subtitle: true,
    startsAt: true,
    propMarketType: true,
    detailUrl: true,
    researchLink: true,
    teams: true,
    completedSummary: true,
}).extend({
    marketPresentation: marketPresentationSchema,
});
export const publicRadarEventsResponseSchema = z.object({
    items: z.array(publicRadarEventListItemSchema),
    nextCursor: z.string().nullable(),
}).strict();
export const publicRadarEventDetailSchema = publicRadarEventListItemSchema.extend({
    researchLinks: z.array(radarResearchLinkSchema),
    worldcup: radarEventWorldCupContextSchema.nullable().optional(),
    completedMatch: radarCompletedMatchDetailSchema.nullable().optional(),
});
export const radarEventEvidencePointSchema = z.object({
    gameMinute: z.number().int().min(0),
    timestamp: z.number().int().positive(),
    polymarketProb: z.number().finite().min(0).max(1),
    medianNetOdds: z.number().finite().gt(1),
    fixtureScore: z.string().trim().min(1).nullable(),
    eventAnnotations: z.array(z.object({
        type: z.string().trim().min(1),
        detail: z.string().trim().min(1),
    })).default([]),
});
export const radarEventEvidenceResponseSchema = z.object({
    eventId: idStringSchema,
    dataSourceNote: z.string().trim().min(1).nullable(),
    points: z.array(radarEventEvidencePointSchema),
});
export const radarWhaleOrderSchema = z.object({
    whaleAddress: z.string(),
    action: z.enum(['buy', 'sell']),
    outcomeLabel: z.string(),
    volumeUsd: z.number(),
    entryPriceCents: z.number(),
    expectedValueBps: z.number().nullable(),
    timestamp: z.string(),
    source: z.string().optional(),
    polymarketEventId: z.string().optional(),
    polymarketMarketId: z.string().optional(),
    polymarketOutcomeId: z.string().nullable().optional(),
    clobTokenId: z.string().nullable().optional(),
    marketSection: z.enum(['moneyline', 'spread', 'total_goals', 'prop']).nullable().optional(),
    lineValue: z.string().nullable().optional(),
});
